// JOBE-0162.5: HumanProfilePage — W3 human profile detail view.
//
// Route: /organization/people/:id (dispatched by PersonSlugRouter).
// Four sections, top-to-bottom:
//   1. Identity head — avatar, name, role · dept link · owner chain
//   2. Contact strip — email, slack DM (audit-emitting)
//   3. Owned roster — agents this human owns (with phase pills)
//   4. Recent activity — mock-only feed; hidden entirely when empty
//
// Reuses ownership.js (countOwnedBy/lookupOwner/ownedBy) and the W3
// directory data from useOrganizationPeople().

import React from 'react';
import { Avatar } from '../components/Avatar.jsx';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { withDrillContext } from '../lib/drillContext.js';
import { ownedBy, lookupOwner } from './ownership.js';
import { emitAudit } from './audit.js';
import { wccOrganizationActivity } from '../mock-wcc.js';
import './organization.css';

const PHASE_TONE = {
  Available: 'ok',
  Degraded: 'warn',
  Disabled: 'bad',
};

const OWNER_CHAIN_LIMIT = 3;

function titleCase(slug) {
  if (!slug) return '';
  return slug
    .split(/[-_\s]+/)
    .map((p) => (p.length === 0 ? '' : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ');
}

/**
 * Walk the ownerId chain up to OWNER_CHAIN_LIMIT hops. When the chain
 * exceeds the cap, render the last visible hop with a "+N more" suffix.
 * Returns an array of `{ row, hop }` ascending the hierarchy.
 */
function walkOwnerChain(rows, startOwnerId) {
  const chain = [];
  const seen = new Set();
  let current = startOwnerId;
  while (current && !seen.has(current) && chain.length < OWNER_CHAIN_LIMIT) {
    const row = lookupOwner(rows, current);
    if (!row) break;
    seen.add(current);
    chain.push(row);
    current = row.ownerId;
  }
  // Count additional hops beyond the cap (for "+N more").
  let extra = 0;
  while (current && !seen.has(current)) {
    const row = lookupOwner(rows, current);
    if (!row) break;
    seen.add(current);
    extra += 1;
    current = row.ownerId;
  }
  return { chain, extra };
}

function OwnerChain({ rows, ownerId }) {
  const { chain, extra } = walkOwnerChain(rows, ownerId);
  if (chain.length === 0) return null;
  return (
    <span className="hp-owner-chain" data-testid="hp-owner-chain">
      {chain.map((row, i) => (
        <span key={row.id}>
          {i === 0 ? ' · reports to ' : ' › '}
          <a
            className="hp-owner-link"
            href={`/organization/people/${row.id}`}
            data-testid={`hp-owner-chain-link-${row.id}`}
          >
            {row.displayName}
          </a>
        </span>
      ))}
      {extra > 0 ? (
        <span className="hp-owner-chain-more" data-testid="hp-owner-chain-more">
          {' '}(+{extra} more)
        </span>
      ) : null}
    </span>
  );
}

function IdentityHead({ row, allRows }) {
  return (
    <header className="hp-head" data-testid="hp-head">
      <Avatar name={row.displayName} size={56} />
      <div className="hp-head-meta">
        <h1 className="hp-name" data-testid="hp-name">{row.displayName}</h1>
        <div className="hp-role-line">
          <span className="hp-role">{row.role}</span>
          <span className="hp-sep">·</span>
          <a
            className="hp-dept"
            data-testid="hp-dept-link"
            href={withDrillContext(`/department/${row.dept}`, {
              from: 'organization-profile',
              personId: row.id,
            })}
          >
            {titleCase(row.dept)}
          </a>
          {row.ownerId ? <OwnerChain rows={allRows} ownerId={row.ownerId} /> : null}
        </div>
      </div>
    </header>
  );
}

function ContactStrip({ row }) {
  if (!row.email && !row.slack) {
    // Render an empty section so layout placement stays predictable
    // (consistent with the spec's "implementer choice, but consistent").
    return <section className="hp-contact" data-testid="hp-contact" />;
  }
  return (
    <section className="hp-contact" data-testid="hp-contact">
      {row.email ? (
        <a
          className="hp-contact-link hp-contact-email"
          data-testid="hp-contact-email"
          href={`mailto:${row.email}`}
        >
          {row.email}
        </a>
      ) : null}
      {row.slack ? (
        <a
          className="hp-contact-link hp-contact-slack"
          data-testid="hp-contact-slack"
          href={`slack://user?team=&id=${row.slack}`}
          onClick={() =>
            emitAudit('slack-handoff', { from: 'organization', target: row.id })
          }
        >
          Send DM <span className="hp-slack-handle">@{row.slack}</span>
        </a>
      ) : null}
    </section>
  );
}

function OwnedRoster({ row, allRows }) {
  const owned = ownedBy(allRows, row.id).filter((r) => r.type === 'agent');
  if (owned.length === 0) {
    return (
      <section className="hp-owned" data-testid="hp-owned">
        <p className="hp-owned-empty" data-testid="hp-owned-empty">
          Owns no agents.
        </p>
      </section>
    );
  }
  return (
    <section className="hp-owned" data-testid="hp-owned">
      <h2 className="hp-owned-head">
        Owns {owned.length} {owned.length === 1 ? 'agent' : 'agents'}
      </h2>
      <ul className="hp-owned-list">
        {owned.map((a) => {
          const tone = a.phase ? PHASE_TONE[a.phase] : null;
          return (
            <li key={a.id} data-testid={`hp-owned-item-${a.id}`}>
              <a
                className="hp-owned-link"
                data-testid={`hp-owned-link-${a.id}`}
                href={withDrillContext(`/agent/${a.id}`, {
                  from: 'organization-profile',
                  personId: row.id,
                })}
              >
                {tone ? <StatusPill tone={tone} label={a.phase} /> : null}
                <span className="hp-owned-name">{a.displayName}</span>
                <span className="hp-owned-role">{a.role}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RecentActivity({ row, activityByPerson }) {
  const entry = (activityByPerson || []).find((a) => a.personId === row.id);
  const events = entry?.events || [];
  if (events.length === 0) return null;
  return (
    <section className="hp-activity" data-testid="hp-activity">
      <h2 className="hp-activity-head">Recent activity</h2>
      <ul className="hp-activity-list">
        {events.map((e, i) => (
          <li key={`${e.ts}-${i}`} className="hp-activity-item">
            <span className="hp-activity-ts">{e.ts}</span>
            <span className="hp-activity-kind-slot">
              <PillChip label={e.kind} />
            </span>
            {e.refUrl ? (
              <a className="hp-activity-text" href={e.refUrl}>
                {e.text}
              </a>
            ) : (
              <span className="hp-activity-text">{e.text}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * @param {object} props
 * @param {object} props.row       The human row from the directory.
 * @param {Array}  props.allRows   Full directory (for owner chain + owned roster).
 * @param {Array}  [props.activity] Optional injected activity fixture (test seam).
 */
export function HumanProfilePage({ row, allRows, activity }) {
  const activitySource = activity ?? wccOrganizationActivity;
  return (
    <div className="hp-page" data-testid="human-profile-page">
      <IdentityHead row={row} allRows={allRows} />
      <ContactStrip row={row} />
      <OwnedRoster row={row} allRows={allRows} />
      <RecentActivity row={row} activityByPerson={activitySource} />
    </div>
  );
}
